<template>
  <div class="job-status-multi-check">
    <kt-autoselect
      :mode="props.mode"
      :label="props.label"
      :options="statusOptions"
      display-by="Label"
      value-by="Id"
      :multiple="true"
      v-model="model"
      search-placeholder="Search status..."
      :empty-text="props.emptyText"
      :on-refresh="handleRefresh"
      @change="handleChange"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, type PropType } from 'vue';
import KtAutoselect from '@/components/UI/KtAutoselect.vue';
import { useJobStatusStore } from '@/stores/JobStatusStore';
import { useStatusManager } from '@/composables/useStatusManager';
import type { JobStatusModel } from '@/models/JobStatusModel';
import type { StatusDefinition } from '@/models/StatusDefinitionModel';

const props = defineProps({
  mode: {
    type: String,
    default: "dropdown"
  },
  label: {
    type: String,
    default: 'Select Status'
  },
  filter: {
    type: Function as PropType<(status: StatusDefinition) => boolean>,
    default: null
  },
  emptyText: {
    type: String,
    default: 'No Matching Statuses'
  },
  useUnified: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits<{
  change: [value: number[]];
}>();

const model = defineModel<number[]>({
  default: () => []
});

// Unified status manager (new system)
const { activeStatuses, reloadStatuses } = useStatusManager('Request');

// Legacy store (old system)
const statusStore = useJobStatusStore();

// Raw list from the store. `statusOptions` derives from it so the filter
// prop is re-applied reactively whenever any reactive state it reads
// changes after initial mount.
const rawStatuses = ref<StatusDefinition[]>([]);

const statusOptions = computed<StatusDefinition[]>(() =>
  props.filter ? rawStatuses.value.filter(props.filter) : rawStatuses.value
);

// Load status options on mount - prefer unified system
onMounted(async () => {
  if (props.useUnified) {
    await reloadStatuses();
    if (activeStatuses.value.length > 0) {
      rawStatuses.value = activeStatuses.value;
      return;
    }
  }
  // Fallback to legacy
  await statusStore.load();
  rawStatuses.value = Object.values(statusStore.list).map(s => ({
    Id: s.Id ?? 0,
    Label: s.Status ?? '',
    Description: (s as any).Description ?? '',
    Color: (s as any).Color ?? '#6c757d',
    Icon: (s as any).Icon ?? null,
    IsActive: true,
    IsRefActive: true,
    SortOrder: (s as any).SortOrder ?? 0,
    Category: 'Request'
  } as StatusDefinition)) || [];
});

const handleChange = (value: number[]) => {
  emit('change', value);
};

const handleRefresh = async () => {
  if (props.useUnified) {
    await reloadStatuses();
    if (activeStatuses.value.length > 0) {
      rawStatuses.value = activeStatuses.value;
      return;
    }
  }
  await statusStore.refresh();
  rawStatuses.value = Object.values(statusStore.list).map(s => ({
    Id: s.Id ?? 0,
    Label: s.Status ?? '',
    Description: (s as any).Description ?? '',
    Color: (s as any).Color ?? '#6c757d',
    Icon: (s as any).Icon ?? null,
    IsActive: true,
    IsRefActive: true,
    SortOrder: (s as any).SortOrder ?? 0,
    Category: 'Request'
  } as StatusDefinition)) || [];
};
</script>
